import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

import { SENDER_INIT } from "./constants";

/**
 * An array of active filters.
 *
 * @typedef {Object[]} activeFilters
 * @property {string} name - The (filter) field name.
 * @property {string} value - The (filter) field value.
 */

/**
 * An array of filter configurations.
 *
 * @typedef {Object[]} fieldDefs
 * @property {function} [getRecordValue=default] - A function to access
 *   the raw value. DO NOT provide an anonymous function.
 * @property {string} label - The label shown on the filter.
 * @property {string} name - The unique filter name used to identify it.
 * @property {string} [helpText] - Additional help text, displayed to the
 *   user.
 * @property {string} [initialValue] - The initial filter value.
 * @property {string} type - The filter type.
 * @property {array} options - Options for select filter types.
 * @property {string} placeholder - Placeholder text displayed to the user.
 * @property {bool} showPlaceholder - Whether or not to show the placeholder.
 */

/*
 * A class to manage and enforce filterState shape for filter component.
 *
 * @param {fieldDefs} fieldDefs - An array of filter field definitions.
 * @param {Object} [options] - an optional options object.
 * @param {Object} [options.activeFilters=[]] - An optional array of active
 *   filters and their values.
 * @param {Object} [options.invertMatches=false] - An optional boolean
 *   indicating whether the filter criteria should be inverted or not.
 * @param {Object} [options.sender=SENDER_INIT] - An optional sender type
 *   indicating whether the filter was called by initialization or a user
 *   change. Used downstream to determine whether to push history or not.
 *
 * @return {FilterState} - A FilterState instance.
 */
class FilterState {
  static isEqualSerialized(serialized1, serialized2) {
    return isEqual(serialized1, serialized2);
  }

  constructor(fieldDefs, options = {}) {
    const {
      activeFilters = [],
      invertMatches = false,
      sender = SENDER_INIT,
    } = options;

    this._fieldDefs = cloneDeep(fieldDefs);
    this.activeFilters = cloneDeep(activeFilters);
    this.invertMatches = invertMatches;
    this.sender = sender;
  }

  /*
   * Only activeFilters.
   */
  get activeFilterCount() {
    return this.activeFilters.length;
  }

  /*
   * Includes invert matches filter.
   */
  get allActiveFilterCount() {
    return this.activeFilterCount + (this.invertMatches ? 1 : 0);
  }

  get inactiveFilters() {
    return this._fieldDefs
      .filter((f) => !this.activeFilters.find((af) => af.name === f.name))
      .map((f) => f.name);
  }

  get isFiltered() {
    return Boolean(this.allActiveFilterCount);
  }

  get serializable() {
    return {
      fieldDefs: cloneDeep(this._fieldDefs),
      activeFilters: cloneDeep(this.activeFilters).map((f) => {
        delete f.checkFilterMatch;

        return f;
      }),
      sender: this.sender,
      invertMatches: this.invertMatches,
    };
  }

  isEqual(other) {
    const fs1 = this.serializable;
    const fs2 = other.serializable;

    return this.constructor.isEqualSerialized(fs1, fs2);
  }
}

export { FilterState };
